import { Component } from 'react';
import { reportError, persistErrorLocally } from '../lib/errorReport';

class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null, copied: false };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    console.error('ErrorBoundary caught:', error, errorInfo);
    const errorState = {
      message: String(error?.message || error || 'Unknown error'),
      stack: error?.stack || '',
      component_stack: errorInfo?.componentStack || '',
    };
    persistErrorLocally(error);
    reportError(errorState);
    this.setState({ error: errorState, errorInfo: errorInfo?.componentStack || '' });
  }

  handleReset = () => {
    try {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
    } catch {
      // ignore
    }
    this.setState({ hasError: false, error: null, errorInfo: null, copied: false });
    window.location.href = '/login';
  };

  handleCopy = async () => {
    const text = [
      `Message: ${this.state.error?.message || ''}`,
      ``,
      `URL: ${typeof window !== 'undefined' ? window.location.href : ''}`,
      ``,
      `Stack:`,
      this.state.error?.stack || '',
      ``,
      `Component stack:`,
      this.state.errorInfo || '',
    ].join('\n');
    try {
      await navigator.clipboard.writeText(text);
      this.setState({ copied: true });
      setTimeout(() => this.setState({ copied: false }), 2000);
    } catch {
      // clipboard unavailable
    }
  };

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50">
          <div className="text-center p-8 max-w-lg">
            <h2 className="text-2xl font-bold text-gray-800 mb-2">Something went wrong</h2>
            <p className="text-gray-600 mb-4">
              The application encountered an error. Please try logging in again.
            </p>
            {this.state.error?.message && (
              <pre className="text-left text-xs bg-gray-100 border border-gray-200 rounded-md p-3 mb-4 whitespace-pre-wrap break-words max-h-40 overflow-auto">
                {this.state.error.message}
              </pre>
            )}
            <div className="flex flex-wrap justify-center gap-3">
              <button
                onClick={this.handleReset}
                className="px-6 py-3 bg-slate-900 text-white rounded-lg hover:bg-slate-800 transition-colors"
                data-testid="error-boundary-reset-btn"
              >
                Go to Login
              </button>
              {this.state.error?.message && (
                <button
                  onClick={this.handleCopy}
                  className="px-6 py-3 bg-white border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-100 transition-colors"
                  data-testid="error-boundary-copy-btn"
                >
                  {this.state.copied ? 'Copied!' : 'Copy details'}
                </button>
              )}
            </div>
            <p className="text-xs text-gray-400 mt-4">
              This error was reported automatically to your administrator.
            </p>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
